/**
 * Session form (information meeting).
 */
import * as React from 'react';
import { useQuery } from '@tanstack/react-query';
import { X } from 'lucide-react';
import { SlidePanelForm } from '@/components/common/SlidePanelForm';
import { toLocalDateString } from '@/utils/format-date-utils';
import { Autocomplete } from '@/components/forms/Autocomplete';
import { DatePicker } from '@/components/forms/DatePicker';
import { TimePicker } from '@/components/forms/TimePicker';
import { Input } from '@/components/forms/Input';
import { Select } from '@/components/forms/Select';
import { Checkbox } from '@/components/forms/Checkbox';
import { Textarea } from '@/components/forms/Textarea';
import { UpdateRecurrenceScopeModal } from '@/components/sessions/remote/UpdateRecurrenceScopeModal';
import { useAdminAutocomplete } from '@/hooks/useAdminAutocomplete';
import { useAuthStore } from '@/stores/authStore';
import { previewRecurrence } from '@/services/remote/sessions.service';
import { listScoped as listTutorsRemoteScoped } from '@/services/remote/tutors.service';
import { getSessionTypesForTab } from '@/utils/common-utils';
import {
  SESSION_REMOTE_FORM_DEFAULTS,
  SESSION_REMOTE_FORM_QUERY_KEYS,
  SESSION_REMOTE_MEETING_RECURRENCE_SELECT_OPTIONS,
} from '@/components/sessions/session-configs';
import { useCreateSession, useUpdateSession } from '@/hooks/useSessionsRemote';
import { useFormValidation } from '@/hooks/useFormValidation';
import {
  createSessionRemoteValidationSchema,
  editSessionRemoteValidationSchema,
} from '@/utils/validation-schemas-utils';
import {
  addOneHour,
  animatorRoleCodesForTab,
  appendUniqueById,
  buildCreateSessionDto,
  buildCreateValidationContext,
  buildEditValidationContext,
  buildMeetingRecurrenceFields,
  buildUpdateDto,
  computeParticipantIds,
  firstCreatedSessionId,
  formatMeetingRecurrencePreview,
  getMinTimeForDate,
  initializeFormState,
  isCoordinatorRole,
  isInformationMeetingParticipantLimitReached,
  isMeetingRecurrencePreviewEnabled,
  isRecruiterRole,
  isRecurringRecurrenceType,
  mapScopedUsersToParticipantOptions,
  registerPostCreateParticipants,
  removeById,
  syncIncludeMeSelected,
  todayDateString,
  toggleIncludeMeAnimatorChips,
} from '@/components/sessions/remote/session-form-utils';
import type { ParticipantChip, SessionFormRecord } from '@/types/remote/session.types';
import type { SelectedUser } from '@/types/user.types';
import { toast } from '@/components/ui/toast';
import { getErrorMessage } from '@/utils/errors-utils';

/** Remote tab this form creates and edits. */
const SESSION_TAB = 'information-meeting' as const;

/** Props for {@link InformationMeetingForm} (`create` vs `edit` discriminated). */
export type InformationMeetingFormProps =
  | {
      mode: 'create';
      onClose: () => void;
      onSuccess: () => void;
    }
  | {
      mode: 'edit';
      session: SessionFormRecord;
      sessionId: string;
      onClose: () => void;
      onSuccess: () => void;
    };

/** Session form (information meeting). */
export const InformationMeetingForm: React.FC<InformationMeetingFormProps> = (props) => {
  if (props.mode === 'edit') {
    return <EditInformationMeetingForm {...props} />;
  }
  return <CreateInformationMeetingForm {...props} />;
};

/** Create form for a remote information meeting. */
function CreateInformationMeetingForm({
  onClose,
  onSuccess,
}: Readonly<Extract<InformationMeetingFormProps, { mode: 'create' }>>) {
  const createMutation = useCreateSession();
  const currentUser = useAuthStore((state) => state.user);
  const roles = useAuthStore((state) => state.roles);
  const isCoordinator = isCoordinatorRole(roles);
  const isRecruiter = isRecruiterRole(roles);
  const validation = useFormValidation(createSessionRemoteValidationSchema);
  const availableTypes = getSessionTypesForTab(SESSION_TAB);
  const defaultType = availableTypes[0];

  const [selectedType, setSelectedType] = React.useState(defaultType?.type || '');
  const [sessionDate, setSessionDate] = React.useState('');
  const [startTime, setStartTime] = React.useState('');
  const [endTime, setEndTime] = React.useState('');
  const [recurrenceType, setRecurrenceType] = React.useState('none');
  const [customDates, setCustomDates] = React.useState<string[]>([]);
  const [customDateInput, setCustomDateInput] = React.useState('');
  const [selectedAnimators, setSelectedAnimators] = React.useState<SelectedUser[]>([]);
  const [animatorAutocompleteValue, setAnimatorAutocompleteValue] = React.useState('');
  const [animatorResetKey, setAnimatorResetKey] = React.useState(0);
  const [includeMe, setIncludeMe] = React.useState(isRecruiter);
  const [maxParticipants, setMaxParticipants] = React.useState<number>(
    SESSION_REMOTE_FORM_DEFAULTS.maxParticipants,
  );
  const [selectedTutorParticipants, setSelectedTutorParticipants] = React.useState<SelectedUser[]>(
    [],
  );
  const [tutorParticipantAutocompleteValue, setTutorParticipantAutocompleteValue] =
    React.useState('');
  const [tutorParticipantResetKey, setTutorParticipantResetKey] = React.useState(0);
  const [internalNotes, setInternalNotes] = React.useState('');

  const {
    options: animatorOptions,
    isLoading: animatorsLoading,
    onSearch: onAnimatorSearch,
  } = useAdminAutocomplete({ roleCodes: animatorRoleCodesForTab(SESSION_TAB) });
  const filteredAnimatorOptions = React.useMemo(
    () => animatorOptions.filter((o) => !selectedAnimators.some((a) => a.id === o.value)),
    [animatorOptions, selectedAnimators],
  );

  const [tutorParticipantsSearchQuery, setTutorParticipantsSearchQuery] = React.useState('');
  const { data: tutorParticipantsData, isLoading: tutorParticipantsLoading } = useQuery({
    queryKey: SESSION_REMOTE_FORM_QUERY_KEYS.tutorParticipants(tutorParticipantsSearchQuery),
    queryFn: () =>
      listTutorsRemoteScoped({
        search: tutorParticipantsSearchQuery,
        participationRI: false,
        pageSize: 10,
      }),
  });
  const tutorParticipantOptions = React.useMemo(
    () =>
      mapScopedUsersToParticipantOptions(
        tutorParticipantsData?.data || [],
        selectedTutorParticipants.map((sel) => sel.id),
      ),
    [tutorParticipantsData?.data, selectedTutorParticipants],
  );
  const onTutorParticipantsSearch = React.useCallback((query: string) => {
    setTutorParticipantsSearchQuery(query);
  }, []);

  React.useEffect(() => {
    setSelectedAnimators((prev) => syncIncludeMeSelected(includeMe, currentUser, prev));
  }, [includeMe, currentUser]);

  const validationCtx = buildCreateValidationContext({
    selectedType,
    sessionDate,
    startTime,
    tutorId: '',
    studentId: '',
    managedById: null,
    recurrenceType,
    endTime,
    programId: '',
    eventType: '',
    sessionType: SESSION_TAB,
    isPrefilledFromMatching: false,
    animatorsCount: selectedAnimators.length,
    singleAnimatorId: '',
  });

  React.useEffect(() => {
    for (const field of Object.keys(createSessionRemoteValidationSchema)) {
      validation.validateField(
        field,
        validationCtx[field as keyof typeof validationCtx],
        validationCtx,
      );
    }
  }, [selectedType, sessionDate, startTime, endTime, selectedAnimators.length, recurrenceType]);

  const todayStr = todayDateString();
  /** Changing start time fills end with start + 1h when end is empty. */
  const handleStartTimeChange = (value: string) => {
    setStartTime(value);
    if (value) setEndTime(addOneHour(value));
  };

  /** Append a unique animator chip from the autocomplete value. */
  const handleAddAnimator = (value: string) => {
    if (!value || selectedAnimators.some((a) => a.id === value)) return;
    const option = animatorOptions.find((o) => o.value === value);
    if (option) {
      setSelectedAnimators((prev) => appendUniqueById(prev, { id: value, label: option.label }));
    }
    setAnimatorAutocompleteValue('');
    setAnimatorResetKey((k) => k + 1);
  };

  /** Remove an animator chip; clearing self also unchecks include-me. */
  const handleRemoveAnimator = (id: string) => {
    setSelectedAnimators((prev) => removeById(prev, id));
    if (id === currentUser?.id) setIncludeMe(false);
  };

  /** Append a unique tutor-participant chip from the autocomplete value. */
  const handleAddTutorParticipant = (value: string) => {
    if (!value || selectedTutorParticipants.some((t) => t.id === value)) return;
    const option = tutorParticipantOptions.find((o) => o.value === value);
    if (option) {
      setSelectedTutorParticipants((prev) =>
        appendUniqueById(prev, { id: value, label: option.label }),
      );
    }
    setTutorParticipantAutocompleteValue('');
    setTutorParticipantResetKey((k) => k + 1);
  };

  const totalParticipantsRI = selectedTutorParticipants.length;
  const participantLimitReached = isInformationMeetingParticipantLimitReached(totalParticipantsRI);

  /** Append a custom date to the list. */
  const handleAddCustomDate = () => {
    if (customDateInput && !customDates.includes(customDateInput)) {
      setCustomDates((prev) => [...prev, customDateInput]);
      setCustomDateInput('');
    }
  };

  /** Remove a custom date from the list. */
  const handleRemoveCustomDate = (date: string) => {
    setCustomDates((prev) => prev.filter((d) => d !== date));
  };

  const createTypeMapping = availableTypes.find((t) => t.type === selectedType);
  const recurrenceFields = buildMeetingRecurrenceFields({
    uiType: recurrenceType,
    sessionDate,
    customDates,
  });
  const { data: recurrencePreview } = useQuery({
    queryKey: SESSION_REMOTE_FORM_QUERY_KEYS.recurrencePreview(
      createTypeMapping?.type ?? '',
      sessionDate,
      recurrenceFields.recurrenceType,
      JSON.stringify(recurrenceFields.recurrenceRule ?? null),
    ),
    queryFn: () =>
      previewRecurrence({
        type: createTypeMapping?.type,
        sessionDate,
        recurrenceType: recurrenceFields.recurrenceType,
        recurrenceRule: recurrenceFields.recurrenceRule,
      }),
    enabled:
      isMeetingRecurrencePreviewEnabled({ sessionDate, uiType: recurrenceType, customDates }) &&
      Boolean(createTypeMapping?.type),
  });

  /** Count sentence under the recurrence Select, or nothing. */
  function renderRecurrencePreview(): React.ReactNode {
    const text = formatMeetingRecurrencePreview(recurrencePreview);
    if (!text) return null;
    return <p className="text-sm text-gray-600">{text}</p>;
  }

  /** Validate, then POST create. */
  const handleSubmit = async () => {
    if (!validation.validateForm(validationCtx)) return;

    const mapping = availableTypes.find((t) => t.type === selectedType);
    if (!mapping) return;

    const dto = buildCreateSessionDto({
      mapping,
      sessionType: SESSION_TAB,
      sessionDate,
      startTime,
      endTime,
      recurrenceType,
      customDates,
      planDuringHolidays: false,
      tutorId: '',
      studentId: '',
      selectedTutorParticipants,
      selectedStudentParticipants: [],
      singleAnimatorId: '',
      selectedAnimators,
      managedById: null,
      maxParticipants,
      internalNotes,
      eventType: '',
      location: '',
      budgetPosts: [],
      etpCount: 0,
      etpUnitCost: 0,
      budgetGrandTotal: 0,
      programId: '',
    });

    try {
      const createdSession = await createMutation.mutateAsync(dto as never);
      const sessionId = firstCreatedSessionId(createdSession);
      const companyId = currentUser?.companyId;
      if (companyId && sessionId) {
        await registerPostCreateParticipants({
          sessionType: SESSION_TAB,
          sessionId,
          companyId,
          selectedTutorParticipants,
          selectedStudentParticipants: [],
        });
      }
      toast.success('Séance créée avec succès !');
      onSuccess();
    } catch (err: unknown) {
      toast.error(getErrorMessage(err));
    }
  };

  return (
    <SlidePanelForm
      formType="create"
      title="Créer une réunion d'information"
      subtitle="Renseignez les informations de la réunion d'information"
      onClose={onClose}
      onSubmit={handleSubmit}
      submitLabel="Créer"
      isSubmitting={createMutation.isPending}
      isValid={validation.isFormValid()}
    >
      <div className="space-y-6 p-6">
        {availableTypes.length > 1 && (
          <div
            className="space-y-4 p-4 rounded-lg border"
            style={{ borderColor: 'var(--color-1-nuance-3)' }}
          >
            <h3 className="font-semibold" style={{ color: 'var(--color-1-nuance-1)' }}>
              Configuration de la séance
            </h3>
            <Select
              label="Type de séance"
              value={selectedType}
              onChange={setSelectedType}
              options={availableTypes.map((t) => ({ value: t.type, label: t.label }))}
            />
          </div>
        )}

        <div
          className="space-y-4 p-4 rounded-lg border"
          style={{ borderColor: 'var(--color-2-nuance-3)' }}
        >
          <h3 className="font-semibold" style={{ color: 'var(--color-2-nuance-1)' }}>
            Planification
          </h3>
          <DatePicker
            label="Date"
            required
            value={sessionDate}
            onChange={setSessionDate}
            minDate={todayStr}
          />
          <div className="grid grid-cols-2 gap-4">
            <TimePicker
              label="Heure de début"
              required
              value={startTime}
              onChange={handleStartTimeChange}
              minTime={getMinTimeForDate(sessionDate, todayStr)}
              step={1}
            />
            <TimePicker
              label="Heure de fin"
              value={endTime}
              onChange={setEndTime}
              minTime={startTime || undefined}
              step={1}
            />
          </div>
          <p className="text-sm text-gray-600 italic">
            Les horaires sont en heure de Paris (Europe/Paris).
          </p>
          <Select
            label="Récurrence"
            required
            value={recurrenceType}
            onChange={setRecurrenceType}
            options={SESSION_REMOTE_MEETING_RECURRENCE_SELECT_OPTIONS}
          />
          {renderRecurrencePreview()}
          {recurrenceType === 'custom' && (
            <div className="space-y-2">
              <div className="flex gap-2">
                <div className="flex-1">
                  <DatePicker
                    label="Ajouter une date"
                    value={customDateInput}
                    onChange={setCustomDateInput}
                    minDate={todayStr}
                  />
                </div>
                <button
                  type="button"
                  onClick={handleAddCustomDate}
                  className="mt-6 px-4 py-2 text-sm font-medium text-white rounded-lg"
                  style={{ backgroundColor: 'var(--color-2-nuance-1)' }}
                >
                  Ajouter
                </button>
              </div>
              {customDates.length > 0 && (
                <div className="flex flex-wrap gap-2">
                  {customDates.map((date) => (
                    <span
                      key={date}
                      className="inline-flex items-center gap-1 px-3 py-1 rounded-lg text-sm"
                      style={{
                        background: 'color-mix(in srgb, var(--color-2-nuance-1) 12%, transparent)',
                        color: 'var(--color-2-nuance-1)',
                        border: '1px solid var(--color-2-nuance-3)',
                      }}
                    >
                      {date}
                      <button
                        type="button"
                        onClick={() => handleRemoveCustomDate(date)}
                        className="ml-1 hover:opacity-70"
                      >
                        <X className="h-3 w-3" />
                      </button>
                    </span>
                  ))}
                </div>
              )}
            </div>
          )}
        </div>

        <div
          className="space-y-4 p-4 rounded-lg border"
          style={{ borderColor: 'var(--color-3-nuance-3)' }}
        >
          <h3 className="font-semibold" style={{ color: 'var(--color-3-nuance-1)' }}>
            Informations générales
          </h3>
          <div>
            <span className="block text-sm font-medium mb-2">
              Animateur(s) <span className="text-red-500">*</span>
            </span>
            <Autocomplete
              key={animatorResetKey}
              placeholder="Rechercher un animateur..."
              options={filteredAnimatorOptions}
              value={animatorAutocompleteValue}
              onChange={handleAddAnimator}
              onSearch={onAnimatorSearch}
              isLoading={animatorsLoading}
              minSearchLength={2}
            />
            {selectedAnimators.length > 0 && (
              <div className="flex flex-wrap gap-2 mt-2">
                {selectedAnimators.map((a) => (
                  <span
                    key={a.id}
                    className="inline-flex items-center gap-1 px-3 py-1 rounded-lg text-sm"
                    style={{
                      background: 'color-mix(in srgb, var(--color-1-nuance-1) 12%, transparent)',
                      color: 'var(--color-1-nuance-1)',
                      border: '1px solid var(--color-1-nuance-3)',
                    }}
                  >
                    {a.label}
                    <button
                      type="button"
                      onClick={() => handleRemoveAnimator(a.id)}
                      className="ml-1 hover:opacity-70"
                    >
                      <X className="w-3 h-3" />
                    </button>
                  </span>
                ))}
              </div>
            )}
            {(isCoordinator || isRecruiter) && (
              <Checkbox
                id="include-me"
                checked={includeMe}
                onCheckedChange={(checked) => setIncludeMe(Boolean(checked))}
                label="S'inclure en tant qu'animateur"
                className="mt-2"
              />
            )}
          </div>

          <Input
            label="Nombre maximum de participants"
            type="number"
            value={maxParticipants}
            onChange={(e) => setMaxParticipants(Number.parseInt(e.target.value))}
            min={1}
            max={SESSION_REMOTE_FORM_DEFAULTS.maxParticipants}
            helperText="Maximum 25 participants pour un accompagnement personnalisé"
          />

          <div
            className="p-3 rounded-lg text-sm font-medium"
            style={{
              backgroundColor: participantLimitReached
                ? 'color-mix(in srgb, rgb(var(--status-error)) 10%, transparent)'
                : 'color-mix(in srgb, var(--color-1-nuance-1) 10%, transparent)',
              color: participantLimitReached
                ? 'rgb(var(--status-error))'
                : 'var(--color-1-nuance-1)',
              border: `1px solid ${participantLimitReached ? 'rgb(var(--status-error))' : 'var(--color-1-nuance-3)'}`,
            }}
          >
            Participants : {totalParticipantsRI}/{SESSION_REMOTE_FORM_DEFAULTS.maxParticipants}
            {participantLimitReached && ` (${getErrorMessage('participantLimitReached')})`}
          </div>

          <div>
            <span className="block text-sm font-medium mb-2">Tuteurs participants (optionnel)</span>
            <Autocomplete
              key={tutorParticipantResetKey}
              placeholder={
                participantLimitReached
                  ? getErrorMessage('participantLimitReached')
                  : 'Rechercher un tuteur...'
              }
              options={tutorParticipantOptions}
              value={tutorParticipantAutocompleteValue}
              onChange={(val) => {
                if (!participantLimitReached) handleAddTutorParticipant(val);
              }}
              onSearch={onTutorParticipantsSearch}
              isLoading={tutorParticipantsLoading}
              minSearchLength={2}
              disabled={participantLimitReached}
              helperText="Seuls les tuteurs en cours d'inscription et non encore inscrits à une réunion d'information sont affichés."
            />
            {selectedTutorParticipants.length > 0 && (
              <div className="flex flex-wrap gap-2 mt-2">
                {selectedTutorParticipants.map((t) => (
                  <span
                    key={t.id}
                    className="inline-flex items-center gap-1 px-3 py-1 rounded-lg text-sm"
                    style={{
                      background: 'color-mix(in srgb, var(--color-2-nuance-1) 12%, transparent)',
                      color: 'var(--color-2-nuance-1)',
                      border: '1px solid var(--color-2-nuance-3)',
                    }}
                  >
                    {t.label}
                    <button
                      type="button"
                      onClick={() => setSelectedTutorParticipants((prev) => removeById(prev, t.id))}
                      className="ml-1 hover:opacity-70"
                    >
                      <X className="w-3 h-3" />
                    </button>
                  </span>
                ))}
              </div>
            )}
          </div>
        </div>

        <div
          className="space-y-2 p-4 rounded-lg border"
          style={{ borderColor: 'var(--color-1-nuance-3)' }}
        >
          <Textarea
            label="Notes internes (optionnel)"
            value={internalNotes}
            onChange={(e) => setInternalNotes(e.target.value)}
            placeholder="Notes ou commentaires internes..."
            rows={3}
          />
        </div>
      </div>
    </SlidePanelForm>
  );
}

/** Edit form for an existing remote information meeting. */
function EditInformationMeetingForm({
  session,
  sessionId,
  onClose,
  onSuccess,
}: Readonly<Extract<InformationMeetingFormProps, { mode: 'edit' }>>) {
  const updateMutation = useUpdateSession();
  const currentUser = useAuthStore((state) => state.user);
  const roles = useAuthStore((state) => state.roles);
  const isCoordinator = isCoordinatorRole(roles);
  const isRecruiter = isRecruiterRole(roles);
  const validation = useFormValidation(editSessionRemoteValidationSchema);

  const [sessionDate, setSessionDate] = React.useState('');
  const [startTime, setStartTime] = React.useState('');
  const [endTime, setEndTime] = React.useState('');
  const [recurrenceType, setRecurrenceType] = React.useState('');
  const [internalNotes, setInternalNotes] = React.useState('');
  const [maxParticipants, setMaxParticipants] = React.useState<number>(
    SESSION_REMOTE_FORM_DEFAULTS.maxParticipants,
  );
  const [includeMe, setIncludeMe] = React.useState(isRecruiter);
  const [selectedAnimators, setSelectedAnimators] = React.useState<ParticipantChip[]>([]);
  const [animatorResetKey, setAnimatorResetKey] = React.useState(0);
  const [selectedTutorParticipants, setSelectedTutorParticipants] = React.useState<
    ParticipantChip[]
  >([]);
  const [tutorParticipantResetKey, setTutorParticipantResetKey] = React.useState(0);
  const [isModalOpen, setIsModalOpen] = React.useState(false);

  const {
    options: animatorOptions,
    isLoading: isLoadingAnimators,
    onSearch: onSearchAnimators,
  } = useAdminAutocomplete({
    roleCodes: animatorRoleCodesForTab(SESSION_TAB),
  });

  const [tutorParticipantsSearchQuery, setTutorParticipantsSearchQuery] = React.useState('');
  const { data: tutorParticipantsData, isLoading: isLoadingTutorParticipants } = useQuery({
    queryKey: SESSION_REMOTE_FORM_QUERY_KEYS.tutorParticipants(tutorParticipantsSearchQuery),
    queryFn: () =>
      listTutorsRemoteScoped({
        search: tutorParticipantsSearchQuery,
        participationRI: false,
        pageSize: 10,
      }),
  });
  const tutorParticipantOptions = React.useMemo(
    () =>
      mapScopedUsersToParticipantOptions(
        tutorParticipantsData?.data || [],
        selectedTutorParticipants.map((sel) => sel.id),
      ),
    [tutorParticipantsData?.data, selectedTutorParticipants],
  );
  const onSearchTutorParticipants = React.useCallback((query: string) => {
    setTutorParticipantsSearchQuery(query);
  }, []);

  React.useEffect(() => {
    initializeFormState(session, {
      setSessionDate,
      setStartTime,
      setEndTime,
      setRecurrenceType,
      setPlanDuringHolidays: () => undefined,
      setInternalNotes,
      setMaxParticipants,
      setSelectedTutor: () => undefined,
      setSelectedStudent: () => undefined,
      setSingleAnimatorId: () => undefined,
      setSingleAnimatorName: () => undefined,
      setSelectedAnimators,
      setSelectedTutorParticipants,
      setSelectedStudentParticipants: () => undefined,
      setManagedById: () => undefined,
      setProgramId: () => undefined,
      setEventType: () => undefined,
      setLocation: () => undefined,
      setBudgetPosts: () => undefined,
      setEtpUnitCost: () => undefined,
    });
    validation.validateInitialFormData({
      sessionDate: toLocalDateString(new Date(session.sessionDate)),
      startTime: session.startTime,
      managedById: null,
      programId: '',
      _isTutoring: false,
    });
  }, [session]);

  React.useEffect(() => {
    const ctx = buildEditValidationContext({
      sessionDate,
      startTime,
      managedById: null,
      programId: '',
      isTutoring: false,
    });
    validation.validateField('sessionDate', sessionDate, ctx);
    validation.validateField('startTime', startTime, ctx);
  }, [sessionDate, startTime]);

  /** Toggle the current user on or off the animator list. */
  const handleIncludeMeChange = React.useCallback(
    (checked: boolean) => {
      setIncludeMe(checked);
      setSelectedAnimators((prev) => toggleIncludeMeAnimatorChips(checked, currentUser, prev));
    },
    [currentUser],
  );

  const totalParticipantsRI = selectedTutorParticipants.length;
  const participantLimitReached = isInformationMeetingParticipantLimitReached(totalParticipantsRI);

  /** Append a unique animator from the edit-mode autocomplete. */
  const handleAddAnimator = (val: string) => {
    const option = animatorOptions.find((o) => o.value === val);
    if (option && !selectedAnimators.some((a) => a.id === option.value)) {
      setSelectedAnimators((prev) => [
        ...prev,
        { id: option.value, name: option.label, role: 'recruiter' },
      ]);
      setAnimatorResetKey((k) => k + 1);
    }
  };

  /** Validate, then PATCH edit. */
  const handleSubmit = async () => {
    const ctx = buildEditValidationContext({
      sessionDate,
      startTime,
      managedById: null,
      programId: '',
      isTutoring: false,
    });
    if (!validation.validateForm(ctx)) return;

    if (isRecurringRecurrenceType(session.recurrenceType)) {
      setIsModalOpen(true);
      return;
    }

    await executeUpdate(false);
  };

  /** Handle recurrence scope confirmation. */
  const handleConfirmScope = async (scope: 'occurrence' | 'following') => {
    setIsModalOpen(false);
    await executeUpdate(scope === 'following');
  };

  /** Execute the update mutation with the computed DTO. */
  const executeUpdate = async (updateFutureOccurrences: boolean) => {
    const { tutorIds, studentIds, animatorIds } = computeParticipantIds({
      session,
      isTutoring: false,
      selectedTutor: null,
      selectedStudent: null,
      selectedTutorParticipants,
      selectedStudentParticipants: [],
      selectedAnimators,
      singleAnimatorId: '',
    });

    const dto = buildUpdateDto({
      session,
      sessionDate,
      startTime,
      endTime,
      recurrenceType: session.recurrenceType || '',
      planDuringHolidays: false,
      internalNotes,
      maxParticipants,
      tutorIds,
      studentIds,
      animatorIds,
      managedById: null,
      programId: '',
      eventType: '',
      location: '',
      budgetPosts: [],
      etpCount: 0,
      etpUnitCost: 0,
      budgetGrandTotal: 0,
      updateFutureOccurrences,
    });

    try {
      await updateMutation.mutateAsync({ id: sessionId, dto });
      toast.success('Séance modifiée avec succès !');
      onSuccess();
    } catch (err: unknown) {
      toast.error(getErrorMessage(err));
    }
  };

  return (
    <SlidePanelForm
      formType="edit"
      title="Modifier la réunion d'information"
      subtitle="Modifiez les informations de la réunion d'information"
      onClose={onClose}
      onSubmit={handleSubmit}
      submitLabel="Enregistrer"
      isSubmitting={updateMutation.isPending}
      isValid={validation.isFormValid()}
    >
      <div className="space-y-6 p-6">
        <div
          className="space-y-4 p-4 rounded-lg border"
          style={{ borderColor: 'var(--color-2-nuance-3)' }}
        >
          <h3 className="font-semibold text-lg" style={{ color: 'var(--color-2-nuance-1)' }}>
            Planification
          </h3>
          <DatePicker label="Date" value={sessionDate} onChange={setSessionDate} required />
          <div className="grid grid-cols-2 gap-4">
            <TimePicker
              label="Heure de début"
              value={startTime}
              onChange={setStartTime}
              required
              step={1}
            />
            <TimePicker label="Heure de fin" value={endTime} onChange={setEndTime} step={1} />
          </div>
          <p className="text-sm text-gray-600 italic">
            Les horaires sont en heure de Paris (Europe/Paris).
          </p>
          <Select
            label="Récurrence"
            value={recurrenceType}
            onChange={() => {}}
            options={SESSION_REMOTE_MEETING_RECURRENCE_SELECT_OPTIONS}
            disabled
          />
          <Input
            type="number"
            label="Nombre maximum de participants"
            value={maxParticipants.toString()}
            onChange={(e) => setMaxParticipants(Number.parseInt(e.target.value, 10))}
            min={1}
            max={SESSION_REMOTE_FORM_DEFAULTS.maxParticipants}
          />
        </div>

        <div
          className="space-y-4 p-4 rounded-lg border"
          style={{ borderColor: 'var(--color-1-nuance-3)' }}
        >
          <h3 className="font-semibold text-lg" style={{ color: 'var(--color-1-nuance-1)' }}>
            Animateurs ({selectedAnimators.length})
          </h3>
          <Autocomplete
            key={animatorResetKey}
            placeholder="Ajouter un animateur..."
            options={animatorOptions.filter(
              (opt) => !selectedAnimators.some((a) => a.id === opt.value),
            )}
            value=""
            onChange={handleAddAnimator}
            onSearch={onSearchAnimators}
            isLoading={isLoadingAnimators}
            minSearchLength={2}
          />
          {selectedAnimators.length > 0 && (
            <div className="flex flex-wrap gap-2 mt-2">
              {selectedAnimators.map((animator) => (
                <div
                  key={animator.id}
                  className="inline-flex items-center gap-2 px-3 py-1.5 rounded-lg bg-[var(--color-1-nuance-1)] text-white"
                >
                  <span className="text-sm">{animator.name}</span>
                  <button
                    type="button"
                    onClick={() => {
                      setSelectedAnimators((prev) => removeById(prev, animator.id));
                      if (animator.id === currentUser?.id) setIncludeMe(false);
                    }}
                    className="hover:bg-white/20 rounded p-0.5"
                  >
                    <X className="w-3.5 h-3.5" />
                  </button>
                </div>
              ))}
            </div>
          )}
          {(isCoordinator || isRecruiter) && (
            <Checkbox
              id="include-me-edit"
              checked={includeMe}
              onCheckedChange={(checked) => handleIncludeMeChange(Boolean(checked))}
              label="S'inclure en tant qu'animateur"
            />
          )}
        </div>

        <div
          className="space-y-4 p-4 rounded-lg border"
          style={{ borderColor: 'var(--color-2-nuance-3)' }}
        >
          <h3 className="font-semibold text-lg" style={{ color: 'var(--color-2-nuance-1)' }}>
            Tuteurs participants ({selectedTutorParticipants.length})
          </h3>
          <div
            className="p-3 rounded-lg text-sm font-medium"
            style={{
              backgroundColor: participantLimitReached
                ? 'color-mix(in srgb, rgb(var(--status-error)) 10%, transparent)'
                : 'color-mix(in srgb, var(--color-1-nuance-1) 10%, transparent)',
              color: participantLimitReached
                ? 'rgb(var(--status-error))'
                : 'var(--color-1-nuance-1)',
              border: `1px solid ${participantLimitReached ? 'rgb(var(--status-error))' : 'var(--color-1-nuance-3)'}`,
            }}
          >
            Participants : {totalParticipantsRI}/{SESSION_REMOTE_FORM_DEFAULTS.maxParticipants}
            {participantLimitReached && ` (${getErrorMessage('participantLimitReached')})`}
          </div>
          <Autocomplete
            key={tutorParticipantResetKey}
            placeholder={
              participantLimitReached
                ? getErrorMessage('participantLimitReached')
                : 'Ajouter un tuteur participant...'
            }
            options={tutorParticipantOptions}
            value=""
            onChange={(val) => {
              if (participantLimitReached) return;
              const option = tutorParticipantOptions.find((o) => o.value === val);
              if (option && !selectedTutorParticipants.some((t) => t.id === option.value)) {
                setSelectedTutorParticipants((prev) => [
                  ...prev,
                  { id: option.value, name: option.label, role: 'tutor' },
                ]);
                setTutorParticipantResetKey((k) => k + 1);
              }
            }}
            onSearch={onSearchTutorParticipants}
            isLoading={isLoadingTutorParticipants}
            minSearchLength={2}
            disabled={participantLimitReached}
            helperText="Seuls les tuteurs en cours d'inscription et non encore inscrits à une réunion d'information sont affichés."
          />
          {selectedTutorParticipants.length > 0 && (
            <div className="flex flex-wrap gap-2 mt-2">
              {selectedTutorParticipants.map((tutor) => (
                <div
                  key={tutor.id}
                  className="inline-flex items-center gap-2 px-3 py-1.5 rounded-lg bg-[var(--color-2-nuance-1)] text-white"
                >
                  <span className="text-sm">{tutor.name}</span>
                  <button
                    type="button"
                    onClick={() =>
                      setSelectedTutorParticipants((prev) => removeById(prev, tutor.id))
                    }
                    className="hover:bg-white/20 rounded p-0.5"
                  >
                    <X className="w-3.5 h-3.5" />
                  </button>
                </div>
              ))}
            </div>
          )}
        </div>

        <div
          className="space-y-4 p-4 rounded-lg border"
          style={{ borderColor: 'var(--color-1-nuance-3)' }}
        >
          <h3 className="font-semibold text-lg" style={{ color: 'var(--color-1-nuance-1)' }}>
            Notes internes
          </h3>
          <Textarea
            value={internalNotes}
            onChange={(e) => setInternalNotes(e.target.value)}
            placeholder="Notes ou commentaires internes..."
            rows={3}
          />
        </div>
      </div>
      <UpdateRecurrenceScopeModal
        open={isModalOpen}
        onClose={() => setIsModalOpen(false)}
        onConfirm={handleConfirmScope}
      />
    </SlidePanelForm>
  );
}
